import React, { useState } from "react";
import { useParams } from "react-router-dom";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import Im from "immutable";

import { BSBtnDropdown, BSCard } from "traec-react/utils/bootstrap";

import { CompanyPermission } from "../permissions";
import { Spinner } from "traec-react/utils/entities";
import CompanyAvailableIndicators from "./available";
import CompanyIndicatorRow from "./indicatorRow";
import { alertSuccess } from "traec-react/utils/sweetalert";
import { useFullIds, useCompanyPermissions } from "storybook-dashboard/utils/hooks";

const pushAllToChildren = (companyId, _type = "Indicator") => {
  doFetch(`/api/company/${companyId}/dispatch/`, "POST", {
    type: `ADD_${_type.toUpperCase()}_TO_ALL`,
    payload: {
      [`${_type.toLowerCase()}Id`]: "all",
    },
  }).then((response) => {
    if (response.ok) {
      response.json().then((data) => {
        alertSuccess({
          title: `${_type} push success`,
          text: `Succesfully start asynchronous push of all ${_type} to children.\nThis will take approximately ${
            (data?.payload?.estTime || 0) / 60 || "unknown"
          } minutes to complete.`,
          onConfirm: (e) => {
            location.reload();
          },
        });
      });
    }
  });
};

// Loading spinner component
function CompanyIndicatorsLoading() {
  return <Spinner title="Loading..." explanation="Loading Indicator List. It might take a moment" />;
}

// Component to display available indicators list
function AddExistingList({ showAddExistingForm, companyId, toggleAddExisting }) {
  if (!showAddExistingForm) {
    return null;
  }
  return (
    <BSCard
      title="All Available Indicators from Sub-Projects"
      body={<CompanyAvailableIndicators companyId={companyId} toggleShowHandler={toggleAddExisting} />}
    />
  );
}

// Component to render the list of indicators
function IndicatorList({ companyIndicators, companyId, metricTargetMap, baseMetrics }) {
  if (!companyIndicators) {
    return null;
  }
  return companyIndicators
    .toList()
    .sortBy((i) => i.getIn(["resultBaseMetric", "category"]))
    .map((indicator, i) => {
      let metricId = indicator.getIn(["resultBaseMetric", "uid"]);
      let indicatorTarget = indicator.set("metricTarget", metricTargetMap.get(metricId));
      return (
        <CompanyIndicatorRow key={i} companyId={companyId} indicator={indicatorTarget} baseMetrics={baseMetrics} />
      );
    });
}

// Component to render indicator list headers
function IndicatorListHeaders() {
  return (
    <div className="row">
      <div className="col-sm-4">
        <b>{"Name"}</b>
      </div>
      <div className="col-sm-2">
        <b>{"Category"}</b>
      </div>
      <div className="col-sm-1">
        <b>{"Factor"}</b>
      </div>
      <div className="col-sm-3">
        <b>{"Numerators & Denominators"}</b>
      </div>
      <div className="col-sm-1">
        <b>Target</b> | <b>Warn</b>
      </div>
      <div className="col-sm-1">
        <b>{""}</b>
      </div>
    </div>
  );
}

function CompanyIndicators() {
  const { companyId } = useFullIds();
  const [showAddExistingForm, setShowAddExistingForm] = useState(false);

  // Fetch company data
  const { data: company } = useApi("/api/company/{companyId}/", { companyId });

  // Fetch company indicators
  const { data: companyIndicators } = useApi("/api/company/{companyId}/indicator/", { companyId });

  // Fetch company targets
  const { data: companyTargets } = useApi("/api/company/{companyId}/target/", { companyId });

  // Create metric target map
  const metricTargetMap = (companyTargets || Im.List()).reduce(
    (map, target) => map.set(target.getIn(["metric", "uid"]), target),
    Im.Map()
  );

  const toggleAddExisting = (e) => {
    e.preventDefault();
    setShowAddExistingForm(!showAddExistingForm);
  };

  const dropDownLinks = () => {
    return [
      { name: "Add from Project Indicators", onClick: toggleAddExisting },
      { name: "Push all indicators to Children", onClick: (e) => pushAllToChildren(companyId, "Indicator") },
      { name: "Push all targets to Children", onClick: (e) => pushAllToChildren(companyId, "Target") },
    ];
  };

  if (!company) {
    return <CompanyIndicatorsLoading />;
  }

  return (
    <>
      <h3>Corporate Indicators</h3>
      <p>Set Corporate Indicators here that will be shown on the Dashboard</p>
      <CompanyPermission companyId={companyId} requiresAdmin={true}>
        <BSBtnDropdown header={"Admin"} links={dropDownLinks()} />
      </CompanyPermission>
      <div style={{ clear: "both" }} />
      <AddExistingList
        showAddExistingForm={showAddExistingForm}
        companyId={companyId}
        toggleAddExisting={toggleAddExisting}
      />
      <br />
      <br />
      <IndicatorListHeaders />
      <hr />
      <IndicatorList companyIndicators={companyIndicators} companyId={companyId} metricTargetMap={metricTargetMap} />
    </>
  );
}

export default CompanyIndicators;
